import React from 'react'
import { useI18n } from '../../../i18n'
import { Button } from '../../ui/Button'
import { QuestionEvidenceBindingPanel } from '../questions/QuestionEvidenceBindingPanel'
import { ControlRecommendationsPanel } from './ControlRecommendationsPanel'
import ccStyles from '../../../styles/pages/compliance-center/ComplianceCenterOverview.module.scss'

const PRIMARY_BTN = '!bg-green-600 hover:!bg-green-700 !border-green-600'

type Lang = 'en' | 'ru' | 'es'

type Loc = { en?: string; ru?: string; es?: string }

export type QuestionRow = {
  questionKey: string
  domainKey?: string
  legacyControlCode?: string
  localization: {
    assertion: { en?: string; ru?: string; es?: string }
    description?: { en?: string; ru?: string; es?: string }
  }
  trustCriteria?: string[]
  answerValue?: string | null
  assessorNote?: string | null
}

type Props = {
  lang: Lang
  sessionId: string
  standardCode: string
  question: QuestionRow
  evidenceSufficiencyLabel?: string | null
  expectedEvidenceHint?: string | null
  recommendation?: { localization?: Loc }
  govEvidence?: Array<{ id: string; title?: string; freshnessStatus?: string }>
  answer: string
  note: string
  saving?: boolean
  saveError?: string | null
  onAnswerChange: (v: string) => void
  onNoteChange: (v: string) => void
  onSave: () => void
  onEvidenceLinkedChange?: () => void
  hidePrimarySave?: boolean
  footer?: React.ReactNode
}

function pickAssertion(q: QuestionRow, lang: Lang) {
  const a = q.localization?.assertion
  return String(a?.[lang] || a?.en || '')
}

function pickDesc(q: QuestionRow, lang: Lang) {
  const d = q.localization?.description
  return String(d?.[lang] || d?.en || '')
}

export function ControlQuestionAnswerCard({
  lang,
  sessionId,
  standardCode,
  question,
  evidenceSufficiencyLabel,
  expectedEvidenceHint,
  recommendation,
  govEvidence,
  answer,
  note,
  saving,
  saveError,
  onAnswerChange,
  onNoteChange,
  onSave,
  onEvidenceLinkedChange,
  hidePrimarySave,
  footer,
}: Props) {
  const { t } = useI18n()
  const answered = Boolean(question.answerValue)

  return (
    <section className={`${ccStyles.metricCard} rounded-xl border border-slate-200 bg-white p-4 shadow-sm`}>
      <h3 className={ccStyles.metricTitle}>{t('controlCentric.questions.title', 'Questions')}</h3>
      {evidenceSufficiencyLabel ? (
        <p className="mt-2 text-xs text-slate-600">
          <span className="font-semibold">{t('controlCentric.questions.evidenceStatus', 'Control evidence')}:</span>{' '}
          {evidenceSufficiencyLabel}
        </p>
      ) : null}

      <ul className="mt-3 space-y-2">
        <li>
          <div
            className={`${ccStyles.questionPickCard} w-full px-3 py-2 text-left ${ccStyles.questionPickCardActive} ${answered ? ccStyles.questionPickCardRiskGreen : ''}`.trim()}
            role="group"
            aria-label={pickAssertion(question, lang)}
          >
            <div className={ccStyles.questionPickTitle}>{pickAssertion(question, lang)}</div>
            {question.trustCriteria?.length ? (
              <div className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                {question.trustCriteria.join(' · ')}
              </div>
            ) : null}
          </div>
        </li>
      </ul>

      {pickDesc(question, lang) ? (
        <p className="mt-4 border-t border-slate-100 pt-4 text-sm leading-relaxed text-slate-700">{pickDesc(question, lang)}</p>
      ) : null}

      {expectedEvidenceHint ? (
        <p className={`text-sm leading-relaxed text-slate-700 ${pickDesc(question, lang) ? 'mt-3' : 'mt-4 border-t border-slate-100 pt-4'}`}>
          <span className="font-semibold text-slate-800">
            {t('controlCentric.evidence.expected', 'Expected evidence')}:
          </span>{' '}
          {expectedEvidenceHint}
        </p>
      ) : null}

      <div
        className={`flex flex-wrap gap-2 ${
          pickDesc(question, lang) || expectedEvidenceHint ? 'mt-3' : 'mt-4 border-t border-slate-100 pt-4'
        }`}
      >
        {(['YES', 'PARTIAL', 'NO', 'NOT_APPLICABLE'] as const).map((opt) => (
          <Button
            key={opt}
            type="button"
            size="small"
            color={answer === opt ? 'primary' : 'outline'}
            className={answer === opt ? PRIMARY_BTN : ''}
            onClick={() => onAnswerChange(opt)}
          >
            {opt.replace('_', ' ')}
          </Button>
        ))}
      </div>

      <label className="mt-4 block text-sm">
        <span className="font-semibold text-slate-700">{t('controlCentric.questions.note', 'Assessor note')}</span>
        <textarea
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm shadow-sm"
          rows={2}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
        />
      </label>

      <div className="mt-4 border-t border-slate-100 pt-4">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t('controlCentric.questions.evidenceForQuestion', 'Evidence for this answer')}
        </p>
        <QuestionEvidenceBindingPanel
          sessionId={sessionId}
          domainKey={question.domainKey || 'SOC2_SECURITY'}
          questionKey={question.questionKey}
          canonicalControlCode={question.legacyControlCode}
          standardCode={standardCode}
          answerValue={answer}
          govEvidence={govEvidence}
          variant="enterprise"
          onLinkedChange={onEvidenceLinkedChange}
        />
      </div>

      {recommendation?.localization ? (
        <details className={`${ccStyles.gdprAdvancedPanel} mt-4`} open>
          <summary className={ccStyles.gdprAdvancedSummary}>
            {t('controlCentric.recommendations.title', 'Recommendations')}
          </summary>
          <div className={`${ccStyles.gdprAdvancedBody} mt-2`}>
            <ControlRecommendationsPanel localization={recommendation.localization} lang={lang} />
          </div>
        </details>
      ) : null}

      {saveError ? <p className="mt-2 text-xs text-red-600">{saveError}</p> : null}

      <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
        {!hidePrimarySave ? (
          <Button
            type="button"
            size="small"
            color="primary"
            className={PRIMARY_BTN}
            disabled={saving || !answer}
            onClick={() => onSave()}
          >
            {saving ? t('common.saving', 'Saving…') : t('controlCentric.questions.save', 'Save answer')}
          </Button>
        ) : null}
        {footer}
      </div>
    </section>
  )
}
